import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SqfNav from '../../components/SqfNav';
import foodSafetyService from '../../services/foodSafetyService';
import { useLanguage, t } from '../../context/LanguageContext';

function fmt(d) {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function PlanCard({ planType, plan, onInitialize, onShare, onPrint, lang }) {
  const navigate = useNavigate();
  const label = planType === 'fda' ? 'FDA Plan' : 'USDA Plan';
  const subtitle = planType === 'fda'
    ? '21 CFR Part 117 — Preventive Controls'
    : '9 CFR 417 — HACCP Regulations';

  if (!plan) {
    return (
      <div className="card h-100 border-dashed" style={{ border: '2px dashed #dee2e6' }}>
        <div className="card-body d-flex flex-column align-items-center justify-content-center text-center py-5">
          <div style={{ fontSize: 40, marginBottom: 12 }}>{planType === 'fda' ? '🏛️' : '🌾'}</div>
          <h5 className="fw-semibold mb-1">{label}</h5>
          <p className="text-muted small mb-4">{subtitle}</p>
          <button className="btn btn-primary btn-sm" onClick={() => onInitialize(planType)}>
            + {t('Initialize Plan', 'Inicializar Plan', lang)}
          </button>
        </div>
      </div>
    );
  }

  const activeVersion = plan.Versions?.find(v => v.id === plan.activeVersionId);
  const draftVersion  = plan.Versions?.find(v => v.status === 'draft');
  const hasApproved   = !!plan.activeVersionId;

  return (
    <div className="card h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <span style={{ fontSize: 28, marginRight: 8 }}>{planType === 'fda' ? '🏛️' : '🌾'}</span>
            <span className="fw-semibold fs-6">{label}</span>
          </div>
          <span className={`badge ${plan.status === 'approved' ? 'bg-success' : 'bg-warning text-dark'}`}>
            {plan.status === 'approved' ? t('Approved', 'Aprobado', lang) : t('Draft', 'Borrador', lang)}
          </span>
        </div>
        <div className="text-muted small mb-1">{subtitle}</div>
        <div className="fw-semibold mb-3" style={{ fontSize: 14 }}>{plan.name}</div>

        {activeVersion && (
          <div className="mb-2" style={{ fontSize: 13 }}>
            <span className="text-muted">{t('Active version', 'Versión activa', lang)}: </span>
            <span className="fw-semibold">V{activeVersion.versionNumber}</span>
            {activeVersion.approvedAt && (
              <span className="text-muted ms-2">— {t('approved', 'aprobado', lang)} {fmt(activeVersion.approvedAt)}</span>
            )}
          </div>
        )}
        {draftVersion && (
          <div className="mb-3" style={{ fontSize: 12 }}>
            <span className="badge bg-light text-dark border">
              {t('Draft', 'Borrador', lang)} V{draftVersion.versionNumber} {t('in progress', 'en progreso', lang)}
            </span>
          </div>
        )}

        <div className="d-flex gap-2 mt-auto flex-wrap">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`/sqf/food-safety/${planType}`)}
          >
            {t('Edit Plan', 'Editar Plan', lang)}
          </button>
          {hasApproved && (
            <>
              <button
                className="btn btn-outline-secondary btn-sm"
                title={t('Print / Save as PDF', 'Imprimir / Guardar como PDF', lang)}
                onClick={() => onPrint(planType)}
              >
                🖨 {t('Print', 'Imprimir', lang)}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                title={t('Share with auditors or inspectors', 'Compartir con auditores o inspectores', lang)}
                onClick={() => onShare(planType)}
              >
                🔗 {t('Share', 'Compartir', lang)}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FoodSafetyPlanIndexPage() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [initModal, setInitModal] = useState(null);
  const [initName, setInitName]   = useState('');
  const [initSaving, setInitSaving] = useState(false);

  // Share modal state
  const [showShareModal, setShowShareModal]       = useState(false);
  const [sharePlanType, setSharePlanType]         = useState(null);
  const [shareLinks, setShareLinks]               = useState([]);
  const [shareLinksLoading, setShareLinksLoading] = useState(false);
  const [shareExpiry, setShareExpiry]             = useState(15);
  const [shareNote, setShareNote]                 = useState('');
  const [shareSaving, setShareSaving]             = useState(false);
  const [shareCreated, setShareCreated]           = useState(null);
  const [revoking, setRevoking]                   = useState(null);

  const load = () => {
    setLoading(true);
    foodSafetyService.getPlans()
      .then(setPlans)
      .catch(() => setError(t('Failed to load plans.', 'Error al cargar los planes.', lang)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const fdaPlan  = plans.find(p => p.planType === 'fda')  || null;
  const usdaPlan = plans.find(p => p.planType === 'usda') || null;

  const handleInitialize = (planType) => {
    setInitModal(planType);
    setInitName(planType === 'fda' ? 'FDA Food Safety Plan' : 'USDA HACCP Plan');
  };

  const handleConfirmInit = async () => {
    setInitSaving(true);
    try {
      await foodSafetyService.createPlan({ planType: initModal, name: initName.trim() || (initModal === 'fda' ? 'FDA Food Safety Plan' : 'USDA HACCP Plan') });
      setInitModal(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to initialize plan.', 'Error al inicializar el plan.', lang));
    } finally {
      setInitSaving(false);
    }
  };

  const handlePrint = (planType) => {
    window.open(`/sqf/food-safety/${planType}/print?from=index`, '_blank');
  };

  const handleOpenShare = async (planType) => {
    setSharePlanType(planType);
    setShareCreated(null);
    setShareNote('');
    setShareExpiry(15);
    setShareLinksLoading(true);
    setShowShareModal(true);
    try {
      const links = await foodSafetyService.getShareLinks(planType);
      setShareLinks(links || []);
    } catch {
      setShareLinks([]);
    } finally {
      setShareLinksLoading(false);
    }
  };

  const handleCreateShareLink = async () => {
    setShareSaving(true);
    try {
      const link = await foodSafetyService.createShareLink(sharePlanType, { expiryDays: shareExpiry, note: shareNote.trim() || null });
      const url = `${window.location.origin}/share/food-safety/${link.token}`;
      setShareCreated(url);
      setShareLinks(prev => [{ ...link }, ...prev]);
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to create share link.', 'Error al crear el enlace.', lang));
    } finally {
      setShareSaving(false);
    }
  };

  const handleRevokeShareLink = async (token) => {
    setRevoking(token);
    try {
      await foodSafetyService.revokeShareLink(token);
      setShareLinks(prev => prev.map(l => l.token === token ? { ...l, revokedAt: new Date().toISOString() } : l));
    } catch {
      setError(t('Failed to revoke link.', 'Error al revocar el enlace.', lang));
    } finally {
      setRevoking(null);
    }
  };

  const sharePlanLabel = sharePlanType === 'fda' ? 'FDA Plan' : 'USDA Plan';

  return (
    <div>
      <SqfNav />
      <div className="container-fluid py-4" style={{ maxWidth: 1100 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold mb-0">{t('Food Safety Plans', 'Planes de Inocuidad Alimentaria', lang)}</h4>
            <p className="text-muted small mb-0 mt-1">
              {t('HACCP / Food Safety Plan builder and repository. Covers FDA (PC Rule) and USDA (9 CFR 417) plans.',
                 'Constructor y repositorio de Planes de Inocuidad / HACCP. Cubre planes FDA y USDA.', lang)}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/sqf/food-safety/wizard')}>
            ✨ {t('Guided Setup', 'Configuración Guiada', lang)}
          </button>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-center py-5 text-muted">{t('Loading…', 'Cargando…', lang)}</div>
        ) : (
          <>
            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <PlanCard planType="fda" plan={fdaPlan} onInitialize={handleInitialize} onShare={handleOpenShare} onPrint={handlePrint} lang={lang} />
              </div>
              <div className="col-md-6">
                <PlanCard planType="usda" plan={usdaPlan} onInitialize={handleInitialize} onShare={handleOpenShare} onPrint={handlePrint} lang={lang} />
              </div>
            </div>

            {/* Shared Section Library card */}
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="fw-semibold mb-1">📚 {t('Shared Section Library', 'Biblioteca de Secciones Compartidas', lang)}</h6>
                    <p className="text-muted small mb-0">
                      {t('Sections like GMP, Allergen Control, Recall Plan, Food Defense are shared between both plans. Edit once — updates apply everywhere.',
                         'Secciones como BPM, Control de Alérgenos, Plan de Retiro y Defensa Alimentaria son compartidas. Edita una vez, actualiza en todos.', lang)}
                    </p>
                  </div>
                  <button className="btn btn-outline-secondary btn-sm ms-3"
                    onClick={() => navigate('/sqf/food-safety/library')}>
                    {t('Manage Library', 'Administrar Biblioteca', lang)}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Initialize plan modal */}
      {initModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('Initialize', 'Inicializar', lang)} {initModal === 'fda' ? 'FDA' : 'USDA'} {t('Plan', 'Plan', lang)}
                </h5>
                <button className="btn-close" onClick={() => setInitModal(null)} disabled={initSaving} />
              </div>
              <div className="modal-body">
                <label className="form-label fw-semibold">{t('Plan Name', 'Nombre del Plan', lang)}</label>
                <input
                  className="form-control"
                  value={initName}
                  onChange={e => setInitName(e.target.value)}
                  placeholder={initModal === 'fda' ? 'FDA Food Safety Plan' : 'USDA HACCP Plan'}
                  autoFocus
                />
                <div className="text-muted small mt-2">
                  {initModal === 'fda'
                    ? t('Creates a new FDA Food Safety Plan under 21 CFR Part 117. You can import your existing plan using AI import.',
                        'Crea un nuevo Plan de Inocuidad FDA bajo 21 CFR Parte 117. Puedes importar tu plan existente usando IA.', lang)
                    : t('Creates a new USDA HACCP Plan under 9 CFR 417. You can import your existing plan using AI import.',
                        'Crea un nuevo Plan HACCP USDA bajo 9 CFR 417. Puedes importar tu plan existente usando IA.', lang)}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setInitModal(null)} disabled={initSaving}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleConfirmInit} disabled={initSaving || !initName.trim()}>
                  {initSaving ? t('Creating…', 'Creando…', lang) : t('Create Plan', 'Crear Plan', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share plan modal */}
      {showShareModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔗 {t('Share', 'Compartir', lang)} {sharePlanLabel}</h5>
                <button className="btn-close" onClick={() => setShowShareModal(false)} disabled={shareSaving} />
              </div>
              <div className="modal-body">

                {/* Create new link */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h6 className="fw-semibold mb-3">{t('Create New Share Link', 'Crear Nuevo Enlace', lang)}</h6>
                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Expiry', 'Vencimiento', lang)}</label>
                      <div className="d-flex gap-3">
                        {[15, 30].map(days => (
                          <div key={days} className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              id={`idx-exp-${days}`}
                              checked={shareExpiry === days}
                              onChange={() => setShareExpiry(days)}
                              disabled={shareSaving}
                            />
                            <label className="form-check-label" htmlFor={`idx-exp-${days}`} style={{ fontSize: 13 }}>
                              {days} {t('days', 'días', lang)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Note (optional)', 'Nota (opcional)', lang)}</label>
                      <input
                        className="form-control form-control-sm"
                        value={shareNote}
                        onChange={e => setShareNote(e.target.value)}
                        placeholder={t('e.g. For auditor John Smith', 'ej. Para el auditor Juan Gómez', lang)}
                        disabled={shareSaving}
                      />
                    </div>
                    {shareCreated ? (
                      <div>
                        <div className="alert alert-success py-2 mb-2">
                          <div className="fw-semibold mb-1">✅ {t('Link created!', '¡Enlace creado!', lang)}</div>
                          <div className="d-flex gap-2 align-items-center">
                            <input
                              className="form-control form-control-sm"
                              value={shareCreated}
                              readOnly
                              onClick={e => e.target.select()}
                              style={{ fontSize: 11, fontFamily: 'monospace' }}
                            />
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => navigator.clipboard.writeText(shareCreated)}
                            >
                              {t('Copy', 'Copiar', lang)}
                            </button>
                          </div>
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setShareCreated(null)}>
                          {t('Create Another', 'Crear Otro', lang)}
                        </button>
                      </div>
                    ) : (
                      <button className="btn btn-primary btn-sm" onClick={handleCreateShareLink} disabled={shareSaving}>
                        {shareSaving ? t('Creating…', 'Creando…', lang) : `🔗 ${t('Generate Link', 'Generar Enlace', lang)}`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Existing links */}
                <h6 className="fw-semibold mb-2">{t('Active Links', 'Enlaces Activos', lang)}</h6>
                {shareLinksLoading ? (
                  <div className="text-center py-3 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
                  </div>
                ) : shareLinks.length === 0 ? (
                  <div className="text-muted small py-2">{t('No share links yet.', 'Sin enlaces compartidos.', lang)}</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {shareLinks.map(link => {
                      const url = `${window.location.origin}/share/food-safety/${link.token}`;
                      const expired = new Date() > new Date(link.expiresAt);
                      const revoked = !!link.revokedAt;
                      const active = !expired && !revoked;
                      return (
                        <div key={link.token} className={`border rounded p-2 ${active ? '' : 'opacity-50'}`} style={{ fontSize: 12 }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="d-flex gap-2 align-items-center mb-1 flex-wrap">
                                {revoked
                                  ? <span className="badge bg-danger">Revoked</span>
                                  : expired
                                    ? <span className="badge bg-secondary">Expired</span>
                                    : <span className="badge bg-success">Active</span>
                                }
                                {link.note && <span className="text-muted">{link.note}</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: 11 }}>
                                {t('Expires', 'Vence', lang)}: {new Date(link.expiresAt).toLocaleDateString()}
                                {link.CreatedBy && ` · ${t('by', 'por', lang)} ${link.CreatedBy.name || link.CreatedBy.email}`}
                              </div>
                              {active && (
                                <div className="d-flex gap-1 mt-1">
                                  <input
                                    className="form-control form-control-sm"
                                    value={url}
                                    readOnly
                                    onClick={e => e.target.select()}
                                    style={{ fontSize: 10, fontFamily: 'monospace', maxWidth: 340 }}
                                  />
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    style={{ fontSize: 10, padding: '1px 6px' }}
                                    onClick={() => navigator.clipboard.writeText(url)}
                                  >
                                    {t('Copy', 'Copiar', lang)}
                                  </button>
                                </div>
                              )}
                            </div>
                            {active && (
                              <button
                                className="btn btn-outline-danger btn-sm ms-2"
                                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                disabled={revoking === link.token}
                                onClick={() => handleRevokeShareLink(link.token)}
                              >
                                {revoking === link.token ? '…' : t('Revoke', 'Revocar', lang)}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowShareModal(false)}>
                  {t('Close', 'Cerrar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
